// PROTOTYPE (hero shapes): how the hero invites the visitor to press its
// button, Scia: two glints of gold run down the disc, one down each side,
// from its top to its bottom, gathering speed toward the button under it,
// each with a tail that fades behind it; the button catches their light as
// they meet. Twice. It plays once, over `ms`, when useInvite.js says: never
// longer than 5s, as motion that starts by itself must stop by itself
// within 5s to need no pause control (WCAG 2.2.2).
//
// It tells, `at` ms into it, how warm a client still waiting is, a dot at
// { x, y } on a disc `side` px across (glow, 0 to 1), as far from grey to
// gold as it is warm; and how the button answers (button): `light`, 0 to 1,
// how far it has brightened to the bright gold, and `scale`, how much it
// has grown.

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const smooth = (t) => t * t * (3 - 2 * t);

// When each pass of the glints sets off, in ms, and how long each takes to
// reach the disc's bottom, gathering speed as it goes; past the bottom it
// runs on at the speed it reached, SPEED times its average, so its tail
// runs into the button. A glint lights a client LEAD radians ahead of it,
// softly, and TAIL behind it, fading.
const PASSES = [0, 2000];
const ARRIVE = 1300;
const SPEED = 2;
const LEAD = 0.18;
const TAIL = 1.1;
// The button brightens over RISE ms, at its brightest as a glint reaches
// it, and dims over FALL; it grows with its light, by GROWS of its size at
// most.
const RISE = 90;
const FALL = 560;
const GROWS = 0.035;

// How far a glint runs, in radians from the disc's top down either side:
// it sets off just above it, out of sight.
const SPAN = Math.PI + LEAD;
// When the glints reach the bottom; and how long after that a tail takes
// to leave it.
const ARRIVALS = PASSES.map((start) => start + ARRIVE);
const CLEARS = (TAIL / (SPAN * SPEED)) * ARRIVE;

// Where the glints are `t` ms into a pass.
function head(t) {
  const u = t / ARRIVE;
  return -LEAD + SPAN * (u <= 1 ? u * u : 1 + SPEED * (u - 1));
}

// How lit a client is `behind` radians behind a glint, ahead of it if less
// than 0.
function glint(behind) {
  if (behind < -LEAD) return 0;
  if (behind < 0) return smooth(1 + behind / LEAD);
  return behind < TAIL ? (1 - behind / TAIL) ** 2 : 0;
}

// How bright the button is `t` ms after a glint reaches it, 0 to 1.
function answer(t) {
  const since = t + RISE;
  if (since < 0) return 0;
  return since < RISE ? smooth(since / RISE) : (1 - clamp01((since - RISE) / FALL)) ** 2;
}

export const INVITATION = {
  ms: ARRIVALS[ARRIVALS.length - 1] + Math.max(CLEARS, FALL),
  glow(at, { x, y }, side) {
    const middle = side / 2;
    // The client's angle from the disc's top, down whichever side it is on.
    const down = Math.abs(Math.atan2(x - middle, middle - y));
    return Math.max(...PASSES.map((start) => (at < start ? 0 : glint(head(at - start) - down))));
  },
  // As bright as the brightest glint has it, grown by them all.
  button(at) {
    const lights = ARRIVALS.map((arrival) => answer(at - arrival));
    return { light: Math.max(...lights), scale: 1 + lights.reduce((sum, light) => sum + GROWS * light, 0) };
  },
};
